/**
 * The firm's own words about a document: name, what kind of document it is
 * (which decides what the agents may do with it), description (what it is and
 * when to use it), guidance (how to use it), tags, visas, and the optional
 * draft type hint under More Options.
 *
 * When a suggestion is passed, each field whose proposal differs from the
 * live value shows it inline, right under the input, with Accept / Dismiss.
 * Accept edits the form; the caller saves.
 */
import {
  ChevronDown,
  ChevronRight,
  ChevronsUpDown,
  Plus,
  Sparkles,
  X
} from "lucide-react";
import { useEffect, useState, type FC, type ReactNode } from "react";

import { Button } from "@/components/ui/button";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger
} from "@/components/ui/collapsible";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";

import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";

import { DRAFT_TYPE_LABELS, DraftType } from "../../../api/types/draft";
import {
  CATEGORIES,
  CATEGORY_DESCRIPTION,
  CATEGORY_LABEL,
  isLowConfidence,
  proposedValueFor,
  visasOf
} from "../model";
import type {
  FirmLibraryCategory,
  FirmLibrarySuggestion,
  SuggestionField
} from "../types";
import { canonicalVisa } from "../visaCatalog";
import { TagInput } from "./TagInput";
import { VisaPicker } from "./VisaPicker";

export type EntryFieldValues = {
  name: string;
  description: string;
  guidance: string;
  tags: string[];
  draft_type: string | null;
  /** Empty means any visa. */
  visas: string[];
  category: FirmLibraryCategory | null;
};

const NONE = "__none__";

const DRAFT_TYPE_OPTIONS = Object.values(DraftType)
  .map((value) => ({ value, label: DRAFT_TYPE_LABELS[value] ?? value }))
  .sort((a, b) => a.label.localeCompare(b.label));

const FIELD_LABEL: Record<SuggestionField, string> = {
  name: "name",
  description: "description",
  guidance: "guidance",
  tags: "tags",
  draft_type: "draft type",
  visa: "visa",
  category: "kind"
};

const draftTypeLabel = (value: string) =>
  DRAFT_TYPE_OPTIONS.find((option) => option.value === value)?.label ?? value;

/** Suggestions the form can show. All optional: batch review passes none. */
export type FieldSuggestions = {
  suggestion: FirmLibrarySuggestion | null | undefined;
  dismissed: ReadonlySet<SuggestionField>;
  onAccept: (field: SuggestionField) => void;
  onDismiss: (field: SuggestionField) => void;
  /** Add one suggested tag; without it tags are all-or-nothing. */
  onAcceptTag?: (tag: string) => void;
};

type Props = {
  values: EntryFieldValues;
  onChange: (values: EntryFieldValues) => void;
  disabled?: boolean;
  /** Compact: shorter textareas, for a table row. */
  compact?: boolean;
  idPrefix?: string;
  suggestions?: FieldSuggestions;
  /** What the agents treat it as while `category` is unset (from its tags). */
  fallbackCategory?: FirmLibraryCategory;
};

/** Long proposals are clamped to a few lines until asked for. */
const CLAMP_CHARS = 280;

const FieldSuggestion: FC<{
  field: SuggestionField;
  text: string;
  low: boolean;
  disabled?: boolean;
  onAccept: () => void;
  onDismiss: () => void;
  /** Proposals that can be taken one at a time (tags). */
  chips?: string[];
  onAcceptChip?: (chip: string) => void;
  /** Leads the proposal ("Suggested Name"); Dismiss is then spelled out. */
  label?: string;
}> = ({
  field,
  text,
  low,
  disabled,
  onAccept,
  onDismiss,
  chips,
  onAcceptChip,
  label
}) => {
  const [expanded, setExpanded] = useState(false);
  const clamp = text.length > CLAMP_CHARS && !expanded;
  const shown = clamp ? `${text.slice(0, CLAMP_CHARS).trimEnd()}…` : text;
  const chipped = Boolean(chips?.length && onAcceptChip);
  return (
    <div
      className="flex items-start gap-2 rounded-md bg-accent-soft px-2.5 ring-1 ring-accent-line py-2 text-sm"
      data-testid={`suggested-${field}`}
    >
      <Sparkles className="mt-0.5 size-3.5 shrink-0 text-accent-seed" />
      <div className="min-w-0 flex-1 space-y-1">
        {chipped && chips ? (
          <div className="flex flex-wrap gap-1">
            {chips.map((chip) => (
              <button
                key={chip}
                type="button"
                disabled={disabled}
                className="inline-flex items-center gap-0.5 rounded-full bg-elevated px-2 py-0.5 text-sm ring-1 ring-accent-line hover:bg-accent-soft disabled:opacity-60"
                onClick={() => onAcceptChip?.(chip)}
                aria-label={`Add tag ${chip}`}
                data-testid={`accept-tag-${chip}`}
              >
                <Plus className="size-3" />
                {chip}
              </button>
            ))}
          </div>
        ) : (
          <p className="whitespace-pre-wrap break-words leading-5">
            {label ? (
              <span className="font-medium text-fg-2">{label}: </span>
            ) : null}
            {shown}
          </p>
        )}
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
          {clamp || expanded ? (
            <button
              type="button"
              className="text-fg-3 underline-offset-2 hover:underline"
              onClick={() => setExpanded((v) => !v)}
            >
              {expanded ? "Show less" : "Show more"}
            </button>
          ) : null}
          {low ? (
            <span className="rounded-full bg-warn-soft px-1.5 text-sm text-warn ring-1 ring-warn-line">
              Low confidence
            </span>
          ) : null}
        </div>
      </div>
      {disabled ? null : (
        <div className="flex shrink-0 items-center gap-0.5">
          <Button
            type="button"
            size="sm"
            variant="ghost"
            className="text-accent-seed hover:text-accent-seed"
            onClick={onAccept}
            data-testid={`accept-${field}`}
          >
            {chipped ? "Add All" : "Accept"}
          </Button>
          {label ? (
            <Button
              type="button"
              size="sm"
              variant="ghost"
              className="text-fg-3"
              aria-label={`Dismiss suggested ${FIELD_LABEL[field] ?? field}`}
              onClick={onDismiss}
              data-testid={`dismiss-${field}`}
            >
              Dismiss
            </Button>
          ) : (
            <Button
              type="button"
              size="icon-sm"
              variant="ghost"
              className="size-7 text-fg-3"
              aria-label={`Dismiss suggested ${FIELD_LABEL[field] ?? field}`}
              onClick={onDismiss}
              data-testid={`dismiss-${field}`}
            >
              <X className="size-3.5" />
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

export const EntryFieldsForm: FC<Props> = ({
  values,
  onChange,
  disabled,
  compact,
  idPrefix = "entry",
  suggestions,
  fallbackCategory = "sample"
}) => {
  const set = <K extends keyof EntryFieldValues>(
    key: K,
    value: EntryFieldValues[K]
  ) => onChange({ ...values, [key]: value });

  /** The proposal for a field, formatted for display, or null. */
  const proposalFor = (field: SuggestionField): string | null => {
    if (!suggestions || suggestions.dismissed.has(field)) return null;
    const proposed = proposedValueFor(values, suggestions.suggestion, field);
    if (proposed === null) return null;
    if (Array.isArray(proposed)) return proposed.join(", ");
    if (field === "draft_type") return draftTypeLabel(proposed);
    if (field === "category")
      return CATEGORY_LABEL[proposed as FirmLibraryCategory] ?? proposed;
    return proposed;
  };
  const shownCategory = values.category ?? fallbackCategory;

  // Draft Type is rarely set; it opens on its own when it matters.
  const [moreOpen, setMoreOpen] = useState(false);
  const wantsMore =
    Boolean(values.draft_type) || proposalFor("draft_type") !== null;
  useEffect(() => {
    if (wantsMore) setMoreOpen(true);
  }, [wantsMore]);

  const suggestionFor = (field: SuggestionField): ReactNode => {
    const text = proposalFor(field);
    if (text === null || !suggestions) return null;
    const proposed = proposedValueFor(values, suggestions.suggestion, field);
    return (
      <FieldSuggestion
        field={field}
        text={text}
        low={isLowConfidence(suggestions.suggestion?.confidence?.[field])}
        disabled={disabled}
        onAccept={() => suggestions.onAccept(field)}
        onDismiss={() => suggestions.onDismiss(field)}
        chips={Array.isArray(proposed) ? proposed : undefined}
        onAcceptChip={suggestions.onAcceptTag}
        label={field === "name" ? "Suggested Name" : undefined}
      />
    );
  };

  /** Label with a small dot while a suggestion for the field awaits review. */
  const labelFor = (field: SuggestionField, text: string, htmlFor: string) => (
    <Label htmlFor={htmlFor} className="gap-1.5">
      {text}
      {proposalFor(field) !== null ? (
        <span
          aria-hidden
          className={cn("inline-block size-1.5 rounded-full bg-accent-seed")}
        />
      ) : null}
    </Label>
  );

  return (
    <div className="space-y-3">
      <div className="space-y-1.5">
        {labelFor("name", "Name", `${idPrefix}-name`)}
        <Input
          id={`${idPrefix}-name`}
          value={values.name}
          disabled={disabled}
          placeholder="e.g. O-1A brief for musicians"
          onChange={(event) => set("name", event.target.value)}
        />
        {suggestionFor("name")}
      </div>
      <div className="space-y-1.5">
        {labelFor("category", "What is it?", `${idPrefix}-category`)}
        <RadioGroup
          id={`${idPrefix}-category`}
          value={shownCategory}
          disabled={disabled}
          onValueChange={(value) =>
            set("category", value as FirmLibraryCategory)
          }
          className={cn("grid-cols-1 gap-1.5", !compact && "sm:grid-cols-2")}
          data-testid="category-choice"
        >
          {CATEGORIES.map((category) => {
            const itemId = `${idPrefix}-category-${category}`;
            const selected = shownCategory === category;
            return (
              <Label
                key={category}
                htmlFor={itemId}
                className={cn(
                  "flex cursor-pointer items-start gap-2 rounded-md p-2.5 font-normal ring-1 transition-colors",
                  selected
                    ? "bg-accent-soft ring-accent-line"
                    : "ring-line-2 hover:bg-fill-1",
                  disabled && "cursor-default opacity-70"
                )}
              >
                <RadioGroupItem
                  id={itemId}
                  value={category}
                  className="mt-0.5"
                />
                <span className="space-y-0.5">
                  <span className="block text-sm font-medium">
                    {CATEGORY_LABEL[category]}
                  </span>
                  <span className="block text-sm text-fg-3">
                    {CATEGORY_DESCRIPTION[category]}
                  </span>
                </span>
              </Label>
            );
          })}
        </RadioGroup>
        {values.category === null ? (
          <p className="text-sm text-fg-3">
            Treated as a {CATEGORY_LABEL[fallbackCategory].toLowerCase()} until
            you choose.
          </p>
        ) : null}
        {suggestionFor("category")}
      </div>
      <div className="space-y-1.5">
        {labelFor("description", "Description", `${idPrefix}-description`)}
        <Textarea
          id={`${idPrefix}-description`}
          value={values.description}
          disabled={disabled}
          rows={compact ? 2 : 3}
          placeholder="What this is and when it applies. The agents pick documents by this text."
          onChange={(event) => set("description", event.target.value)}
        />
        {suggestionFor("description")}
      </div>
      <div className="space-y-1.5">
        {labelFor("guidance", "Guidance", `${idPrefix}-guidance`)}
        <Textarea
          id={`${idPrefix}-guidance`}
          value={values.guidance}
          disabled={disabled}
          rows={compact ? 3 : 4}
          placeholder="How to use it: which parts to keep verbatim, what to adapt, what the firm insists on."
          onChange={(event) => set("guidance", event.target.value)}
        />
        {suggestionFor("guidance")}
      </div>
      <div className="space-y-1.5">
        {labelFor("tags", "Tags", `${idPrefix}-tags`)}
        <TagInput
          id={`${idPrefix}-tags`}
          value={values.tags}
          disabled={disabled}
          onChange={(tags) => set("tags", tags)}
          placeholder="e.g. brief, o-1a, filed"
        />
        {suggestionFor("tags")}
      </div>
      <div className="space-y-1.5">
        {labelFor("visa", "Visas", `${idPrefix}-visa`)}
        <VisaPicker
          id={`${idPrefix}-visa`}
          value={values.visas}
          multiple
          disabled={disabled}
          placeholder="Any visa"
          onChange={(next) => set("visas", next)}
        />
        {suggestionFor("visa")}
      </div>
      <Collapsible open={moreOpen} onOpenChange={setMoreOpen}>
        <CollapsibleTrigger asChild>
          <button
            type="button"
            className="inline-flex items-center gap-1 text-sm text-fg-3 hover:text-fg-2"
            data-testid="more-options"
          >
            {moreOpen ? (
              <ChevronDown className="size-3.5" />
            ) : (
              <ChevronRight className="size-3.5" />
            )}
            More Options
          </button>
        </CollapsibleTrigger>
        <CollapsibleContent className="space-y-1.5 pt-2">
          {labelFor("draft_type", "Draft Type", `${idPrefix}-draft-type`)}
          <Select
            value={values.draft_type ?? NONE}
            disabled={disabled}
            onValueChange={(value) =>
              set("draft_type", value === NONE ? null : value)
            }
          >
            <SelectTrigger
              id={`${idPrefix}-draft-type`}
              className="w-full border-0 px-3 inset-ring inset-ring-line-2 data-[size=default]:h-9"
              icon={<ChevronsUpDown className="size-3.5 shrink-0 opacity-50" />}
            >
              <SelectValue placeholder="Any" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={NONE}>Any</SelectItem>
              {DRAFT_TYPE_OPTIONS.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {suggestionFor("draft_type")}
          <p className="text-sm text-fg-3">
            Limits this document to one kind of draft. Leave it on Any unless it
            only fits one.
          </p>
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
};

export const fieldValuesOf = (entry: {
  name: string;
  description: string;
  guidance: string;
  tags: string[];
  draft_type: string | null;
  visa: string | null;
  visas?: string[];
  category?: FirmLibraryCategory | null;
}): EntryFieldValues => ({
  name: entry.name,
  description: entry.description,
  guidance: entry.guidance,
  tags: [...entry.tags],
  draft_type: entry.draft_type,
  visas: visasOf(entry).map((visa) => canonicalVisa(visa) ?? visa),
  category: entry.category ?? null
});

const sameList = (a: string[], b: string[]) =>
  a.length === b.length && a.every((item, index) => item === b[index]);

export const sameFieldValues = (
  a: EntryFieldValues,
  b: EntryFieldValues
): boolean =>
  a.name === b.name &&
  a.description === b.description &&
  a.guidance === b.guidance &&
  a.draft_type === b.draft_type &&
  a.category === b.category &&
  sameList(a.visas, b.visas) &&
  sameList(a.tags, b.tags);
